import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Clock, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { approvalsApi, type ApprovalDetail } from '../../services/api';
import ApprovalContext from './ApprovalContext';

/**
 * One approval request: what is asked, who has voted (and through which
 * channel), when it expires, and — for someone who may decide it — approve or
 * deny. Deciding goes through the same server path as every other channel
 * (EP-26 FE-7): the permission, separation of duties, freshness and quorum are
 * all checked THERE, so this panel only offers what the server says is open to
 * this person and shows the server's answer.
 */
function when(ts?: number | null): string {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleString();
}

function left(ts?: number): string {
  if (!ts) return '';
  const s = ts - Date.now() / 1000;
  if (s <= 0) return 'expired';
  const h = Math.floor(s / 3600);
  return h >= 48 ? `${Math.floor(h / 24)} days left` : h >= 1 ? `${h} h left` : `${Math.max(1, Math.round(s / 60))} min left`;
}

const CLOSED_LABEL: Record<string, string> = {
  committed: 'Approved — the move has been made',
  denied: 'Denied',
  withdrawn: 'Withdrawn by the requester',
  voided: 'Voided — the item changed after the request',
  expired: 'Expired',
  failed: 'Approved, but the move failed its checks — the item is unchanged',
};

export default function ApprovalPanel({ requestId, onChanged, compact = false }: {
  requestId: string;
  onChanged?: (detail: ApprovalDetail | null) => void;
  compact?: boolean;
}) {
  const [detail, setDetail] = useState<ApprovalDetail | null>(null);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<'' | 'approve' | 'deny' | 'withdraw'>('');
  const [denyNeedsReason, setDenyNeedsReason] = useState(false);

  const load = useCallback(() => {
    approvalsApi.get(requestId)
      .then((d) => { setDetail(d); setError(''); })
      .catch((e) => setError(String(e?.message || e)));
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  const decide = async (decision: 'approve' | 'deny') => {
    if (decision === 'deny' && !reason.trim()) {
      setDenyNeedsReason(true);
      return;
    }
    setBusy(decision);
    try {
      const r = await approvalsApi.decide(requestId, decision, reason.trim());
      if (r.status === 'committed') toast.success('Approved — the move has been made.');
      else if (r.status === 'pending') toast.success(`Approved ${r.request.approvals}/${r.request.required}.`);
      else if (r.status === 'denied') toast.success('Denied.');
      else toast.error(r.error || `Request ${r.status}.`);
      setReason('');
      setDenyNeedsReason(false);
    } catch (e: any) {
      toast.error(e?.message || 'Could not record the decision.');
    } finally {
      setBusy('');
      const fresh = await approvalsApi.get(requestId).catch(() => null);
      setDetail(fresh);
      onChanged?.(fresh);
    }
  };

  const withdraw = async () => {
    setBusy('withdraw');
    try {
      await approvalsApi.withdraw(requestId);
      toast.success('Request withdrawn.');
    } catch (e: any) {
      toast.error(e?.message || 'Could not withdraw.');
    } finally {
      setBusy('');
      const fresh = await approvalsApi.get(requestId).catch(() => null);
      setDetail(fresh);
      onChanged?.(fresh);
    }
  };

  if (error) return <p className="text-xs text-red-600">{error}</p>;
  if (!detail) return <p className="text-xs text-gray-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading approval…</p>;

  const open = detail.status === 'pending';
  const approvals = detail.votes.filter((v) => v.decision === 'approve').length;

  return (
    <div className={`rounded-lg border ${open ? 'border-amber-200 dark:border-amber-800' : 'border-gray-200 dark:border-gray-700'} ${compact ? 'p-3' : 'p-4'} space-y-3`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4 text-amber-600" />
          {open ? `Awaiting approval ${approvals}/${detail.required}` : (CLOSED_LABEL[detail.status] || detail.status)}
        </div>
        {open && detail.expiresAt ? (
          <span className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {left(detail.expiresAt)}</span>
        ) : null}
      </div>

      {!compact && <ApprovalContext detail={detail} />}

      <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
        {detail.itemType === 'authority' ? (
          <>
            <dt className="text-gray-500">Agent action</dt>
            <dd className="text-gray-900 dark:text-gray-100">
              <strong>{detail.action?.kind}</strong>{detail.action?.summary ? ` — ${detail.action.summary}` : ''}
            </dd>
            <dt className="text-gray-500">Held because</dt>
            <dd className="text-gray-900 dark:text-gray-100">{detail.action?.refusal}
              {detail.action?.governor?.verdict && detail.action.governor.verdict !== 'not_available'
                ? ` · governor: ${detail.action.governor.verdict}${detail.action.governor.reason ? ` (${detail.action.governor.reason})` : ''}`
                : ''}</dd>
            {detail.action?.agentSession ? (<><dt className="text-gray-500">Agent session</dt><dd className="text-gray-900 dark:text-gray-100 font-mono">{detail.action.agentSession}</dd></>) : null}
            {detail.action?.channel ? (<><dt className="text-gray-500">Ways</dt><dd className="text-gray-900 dark:text-gray-100">{detail.action.channel}</dd></>) : null}
          </>
        ) : detail.evidence ? (
          <>
            <dt className="text-gray-500">Evidence</dt>
            <dd className="text-gray-900 dark:text-gray-100">{detail.evidence.fileName || detail.evidence.evidenceId}
              {detail.evidence.subDimension ? ` · ${detail.evidence.subDimension}` : ''}
              {detail.evidence.researchUrl ? <> · <a href={detail.evidence.researchUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline">open in Research</a></> : null}</dd>
            <dt className="text-gray-500">Uploaded by</dt>
            <dd className="text-gray-900 dark:text-gray-100">{detail.evidence.uploader} (may not decide)</dd>
            <dt className="text-gray-500">Example data</dt>
            <dd className="text-gray-900 dark:text-gray-100">
              {(detail.evidence.findings || []).length ? (
                <ul className="list-disc pl-4">
                  {(detail.evidence.findings || []).map((f, i) => (
                    <li key={i}><span className="font-mono">{f.masked}</span> {f.kind ? `(${f.kind})` : ''}{f.why ? ` — ${f.why}` : ''}</li>
                  ))}
                </ul>
              ) : 'none listed'}
              <span className="block text-[11px] text-gray-500 mt-1">Approve to analyse the file; deny to discard it. Values are masked.</span>
            </dd>
          </>
        ) : (
          <>
            <dt className="text-gray-500">Move to</dt>
            <dd className="text-gray-900 dark:text-gray-100">{detail.to}
              {detail.agentFinish ? ' — an agent finished this; approving closes it' : ''}</dd>
          </>
        )}
        <dt className="text-gray-500">Decided by</dt>
        <dd className="text-gray-900 dark:text-gray-100">holders of <code>{detail.permission}</code> ({detail.required} needed, never the requester)</dd>
        <dt className="text-gray-500">Requested</dt><dd className="text-gray-900 dark:text-gray-100">{detail.requester?.label || detail.requestedBy} · {when(detail.requestedAt)}</dd>
        {detail.expiresAt ? (<><dt className="text-gray-500">Expires</dt><dd className="text-gray-900 dark:text-gray-100">{when(detail.expiresAt)}</dd></>) : null}
        {!open && detail.error ? (<><dt className="text-gray-500">Note</dt><dd className="text-gray-900 dark:text-gray-100">{detail.error}</dd></>) : null}
      </dl>

      {detail.roundPlan?.round && (
        <div className="rounded border border-gray-200 dark:border-gray-700 p-2 text-xs space-y-1">
          <div className="uppercase tracking-wide text-gray-500">What this round trains</div>
          <div>{detail.roundPlan.round.newUnits.length} new unit(s), {detail.roundPlan.round.refreshUnits.length} refreshed — {detail.roundPlan.round.cumulativeUnits} in total;
            {' '}{detail.roundPlan.round.uniqueRows.toLocaleString()} unique rows ({detail.roundPlan.round.rowsConsumed.toLocaleString()} trained).</div>
          <div>{detail.roundPlan.cost?.state === 'ok'
            ? <>Estimated {detail.roundPlan.round.gpuHours} GPU-h{detail.roundPlan.round.costUsd != null ? <>, <strong>${detail.roundPlan.round.costUsd.toFixed(2)}</strong></> : null} on {detail.roundPlan.cost.gpuCount}× {detail.roundPlan.cost.gpu}.</>
            : <span className="text-amber-700">{detail.roundPlan.cost?.message}</span>}</div>
          {!detail.roundPlan.order?.ok && <div className="text-red-600">Its batches cannot be trained in order.</div>}
        </div>)}

      <div>
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Votes</div>
        {detail.votes.length === 0 ? (
          <p className="text-xs text-gray-500">No votes yet.</p>
        ) : (
          <ul className="space-y-1">
            {detail.votes.map((v, i) => (
              <li key={i} className="text-xs text-gray-800 dark:text-gray-200 flex items-start gap-1.5">
                {v.decision === 'approve'
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                  : <XCircle className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" />}
                <span>
                  <strong>{v.by}</strong> {v.decision === 'approve' ? 'approved' : 'denied'}
                  {v.channel ? <span className="text-gray-500"> via {v.channel}</span> : null}
                  <span className="text-gray-500"> · {when(v.at)}</span>
                  {v.reason ? <span className="block text-gray-600 dark:text-gray-400">“{v.reason}”</span> : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {open && detail.canDecide ? (
        <div className="space-y-2">
          <label htmlFor={`reason-${requestId}`} className="text-xs text-gray-600 dark:text-gray-400">
            Reason {denyNeedsReason ? <span className="text-red-600">(required to deny)</span> : '(required to deny)'}
          </label>
          <textarea
            id={`reason-${requestId}`}
            value={reason}
            onChange={(e) => { setReason(e.target.value); if (e.target.value.trim()) setDenyNeedsReason(false); }}
            rows={2}
            className={`w-full px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:text-gray-100 ${denyNeedsReason ? 'border-red-400' : 'border-gray-200 dark:border-gray-600'}`}
          />
          <div className="flex gap-2">
            <button type="button" disabled={!!busy} onClick={() => decide('approve')}
              className="px-3 py-1.5 text-xs rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
              {busy === 'approve' ? 'Approving…' : 'Approve'}
            </button>
            <button type="button" disabled={!!busy} onClick={() => decide('deny')}
              className="px-3 py-1.5 text-xs rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
              {busy === 'deny' ? 'Denying…' : 'Deny'}
            </button>
          </div>
        </div>
      ) : open && detail.cannotDecide ? (
        <p className="text-xs text-gray-500">{detail.cannotDecide}</p>
      ) : null}

      {open && detail.isRequester ? (
        <button type="button" disabled={!!busy} onClick={withdraw}
          className="px-3 py-1.5 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
          {busy === 'withdraw' ? 'Withdrawing…' : 'Withdraw request'}
        </button>
      ) : null}
    </div>
  );
}
